import { useInView } from "react-intersection-observer";

import React, { useEffect, useState } from "react";
import { useMutation } from "@apollo/react-hooks";
import { bool, string } from "prop-types";

import { bgMouseGrey } from "../../assets";
import CREATE_LISTING_RECOMMENDATION from "../../mutations/createListingRecommendation";
import SAVE from "../../mutations/save";
import { listingPropType } from "../../propTypes";
import * as S from "./styled";

export default function RecommendedListing({
  listing,
  trackingId,
  wasHelpful
}) {
  const { inView, ref } = useInView({
    triggerOnce: true,
    threshold: 0
  });

  const { inView: inView75percentage, ref: refAnalytics } = useInView({
    triggerOnce: true,
    threshold: 0.75
  });

  const [isSaved, setIsSaved] = useState(listing.is_saved);

  const [wasHelpfulSelected, setWasHelpfulSelected] = useState(
    wasHelpful !== null
  );

  const [showMore] = useMutation(CREATE_LISTING_RECOMMENDATION);
  const [saveListing] = useMutation(SAVE);

  const handleSaveListing = () => {
    const currentSavedState = isSaved;

    setIsSaved(!currentSavedState);

    saveListing({
      variables: {
        item_id: listing.id,
        item_type: listing.__typename.toLowerCase(),
        toggle_save: true
      }
    }).catch(() => {
      setIsSaved(currentSavedState);
    });
  };

  const listingType = listing.__typename.toLowerCase();

  const handleShowMore = ({ isHelpful, listingId }) => {
    showMore({
      variables: {
        helpful: isHelpful,
        listing_id: listingId,
        listing_type: listingType
      }
    });

    setWasHelpfulSelected("confirmed");

    setTimeout(() => {
      setWasHelpfulSelected(true);
    }, 2000);

    window.dataLayer.push({
      event: "custom_event",
      event_category: "recommended_listings",
      event_action: isHelpful ? "helpful" : "not_helpful",
      event_label: `listing_type=${listingType}|listing_id=${listingId}|rec_id=${trackingId}`
    });
  };

  const handleAnalyticsListingCardClick = () => {
    window.dataLayer.push({
      event: "custom_event",
      event_category: "recommended_listings",
      event_action: "click",
      event_label: `listing_type=${listingType}|listing_id=${listing.id}|rec_id=${trackingId}`
    });
  };

  useEffect(() => {
    if (inView75percentage) {
      window.dataLayer.push({
        event: "custom_event",
        event_category: "recommended_listings",
        event_action: "impression",
        event_label: `listing_type=${listingType}|listing_id=${listing.id}|rec_id=${trackingId}`
      });
    }
  }, [inView75percentage]);

  return (
    <div ref={refAnalytics}>
      <S.SlideContainer ref={ref}>
        <S.Listing
          theme="verticalCard"
          {...listing}
          area={listing.area.name}
          imgSrc={inView ? listing.imgSrc : bgMouseGrey}
          isSaved={isSaved}
          onCardClick={handleAnalyticsListingCardClick}
          onSaveClick={() => handleSaveListing(listing.id)}
          title={`${listing.address.pretty_address}${
            listing.address.unit ? ` ${listing.address.unit}` : ""
          }`}
          url={`${listing.url}?rec_id=${trackingId}`}
        />
        {wasHelpfulSelected === "confirmed" && (
          <S.SeeMoreSection>
            <S.SeeMoreCopy>Thank you!</S.SeeMoreCopy>
          </S.SeeMoreSection>
        )}
        {!wasHelpfulSelected && (
          <S.SeeMoreSection>
            <S.SeeMoreCopy>See more like this?</S.SeeMoreCopy>
            <S.SeeMoreButton
              onClick={() =>
                handleShowMore({
                  isHelpful: true,
                  listingId: listing.id
                })
              }
            >
              Yes
            </S.SeeMoreButton>
            <S.SeeMoreDivider>&middot;</S.SeeMoreDivider>
            <S.SeeMoreButton
              onClick={() =>
                handleShowMore({
                  isHelpful: false,
                  listingId: listing.id
                })
              }
            >
              No
            </S.SeeMoreButton>
          </S.SeeMoreSection>
        )}
      </S.SlideContainer>
    </div>
  );
}

RecommendedListing.propTypes = {
  isMobile: bool,
  listing: listingPropType.isRequired,
  trackingId: string,
  wasHelpful: bool
};

RecommendedListing.defaultProps = {
  isMobile: false,
  trackingId: null,
  wasHelpful: null
};
